import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { AnchoredMenu } from './AnchoredMenu.js';
import { Icon } from './Icon.js';
import { cn } from '../lib/cn.js';

/**
 * UI-007 — the dropdown, drawn by us.
 *
 * A native `<select>` takes our styling on the closed control and none at all
 * on the open one: the list is painted by the operating system, in the system
 * font, at the system size, with the system highlight colour. On a Hebrew RTL
 * screen with a custom accent and a dark theme it is the one surface in the
 * product that looks like it came from somewhere else — and on Windows it is
 * the grey 1990s list every screenshot of this app kept catching.
 *
 * So the list is ours. What that costs is everything the platform was doing for
 * free, and each piece is paid for below: keyboard operation, the roles a
 * screen reader needs, typeahead, and a popup that escapes its scroll container
 * without running off the screen. What it buys is one dropdown that matches the
 * rest of the app in both themes, both directions and any accent.
 *
 * The API is deliberately still shaped like a `<select>` — `value`, `onChange`,
 * `<option>` children — because that is what every call site already reads
 * like, and a dropdown nobody can use without reading its source is not an
 * improvement. `onChange` hands over the value itself rather than an event: no
 * event exists here, and inventing one that only carries `target.value` would
 * be a lie a caller could reasonably dereference further.
 */

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  /** `<option>` elements, so call sites keep reading like markup. */
  children?: ReactNode;
  /** Same list as data, for callers that already hold one. */
  options?: SelectOption[];
  /** Shown when `value` matches no option. Without one the trigger reads empty. */
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /*
   * AUTH-002 — a box at the top of the list that narrows it as you type.
   *
   * Off by default: below a dozen options a search field is one more thing to
   * read past. Worth turning on where the list is long enough that finding a
   * row means scanning it — the 35 trades on the signup form are the case this
   * was built for, and the first letter of a Hebrew trade is a poor index into
   * them ("שיפוצים", "שיווק דיגיטלי", "שיש ואבן").
   */
  searchable?: boolean;
  /** What the search box says when empty. Supplied by the caller — i18n lives there. */
  searchPlaceholder?: string;
  /** Shown in place of the list when the search matches nothing. */
  emptyText?: string;
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** Flattens fragments and arrays, because `{cond && <option/>}` and `.map()` are both normal. */
function collect(children: ReactNode, into: SelectOption[]): void {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'option') {
      const props = (child as ReactElement<{ value?: string | number; disabled?: boolean; children?: ReactNode }>)
        .props;
      into.push({
        value: String(props.value ?? ''),
        label: props.children ?? String(props.value ?? ''),
        disabled: props.disabled,
      });
      return;
    }
    collect((child.props as { children?: ReactNode }).children, into);
  });
}

/** Typeahead only works on text; an option labelled with a node has nothing to match. */
const textOf = (label: ReactNode): string => (typeof label === 'string' ? label : '');

export function Select({
  value,
  onChange,
  children,
  options,
  placeholder,
  disabled = false,
  invalid = false,
  searchable = false,
  searchPlaceholder,
  emptyText,
  className = '',
  id,
  name,
  ...aria
}: SelectProps) {
  const items: SelectOption[] = options ?? [];
  if (!options) collect(children, items);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const listId = useId();

  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(0);
  const [query, setQuery] = useState('');

  /*
   * Everything below indexes the *visible* list, not the full one. An index
   * into `items` would point at a different row the moment the search narrowed
   * it, and the row the keyboard moved to would not be the row that got picked.
   */
  const needle = query.trim().toLowerCase();
  const visible =
    searchable && needle
      ? items.filter((item) => textOf(item.label).toLowerCase().includes(needle))
      : items;
  const selectedIndex = visible.findIndex((item) => item.value === value);
  const [active, setActive] = useState(selectedIndex);

  /* The popup lines up with the control that opened it: a filter sized to its
     own content would otherwise open a list of a different width beside it. */
  useLayoutEffect(() => {
    if (open) setWidth(Math.max(triggerRef.current?.getBoundingClientRect().width ?? 0, 160));
  }, [open]);

  /* Opening lands on what is currently chosen, not on the top of the list —
     the same place a native select opens, and the only position from which
     "one down" means anything. */
  useEffect(() => {
    if (open) setActive(selectedIndex === -1 ? 0 : selectedIndex);
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  /* A search left over from last time is a list that opens already narrowed
     with no sign of why, so the box is emptied on the way out. */
  useEffect(() => {
    if (!open) setQuery('');
    else if (searchable) searchRef.current?.focus();
  }, [open, searchable]);

  /* Narrowing moves the ground under the cursor: whatever it was on is
     probably gone, and the first surviving row is the only sensible place. */
  useEffect(() => {
    if (open && needle) setActive(0);
  }, [open, needle]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const pick = (index: number) => {
    const item = visible[index];
    if (!item || item.disabled) return;
    onChange(item.value);
    close();
  };

  /** Next selectable index in `step` direction, skipping disabled rows. */
  const step = (from: number, direction: 1 | -1): number => {
    for (let i = from + direction; i >= 0 && i < visible.length; i += direction) {
      if (!visible[i]?.disabled) return i;
    }
    return from;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const key = event.key;

    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (key === 'Escape' || key === 'Tab') {
      // Tab closes rather than trapping: the list is a popup, not a dialog, and
      // a keyboard user moving on must not have to dismiss it first. Escape
      // stops here — Modal closes on a document-level Escape, so without this a
      // dropdown inside a dialog would take the dialog down with it. Tab keeps
      // travelling, because that same dialog still owns the focus cycle.
      if (key === 'Escape') event.stopPropagation();
      setOpen(false);
      return;
    }
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      pick(active);
      return;
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      setActive(step(active, key === 'ArrowDown' ? 1 : -1));
      return;
    }
    if (key === 'Home' || key === 'End') {
      event.preventDefault();
      setActive(key === 'Home' ? step(-1, 1) : step(visible.length, -1));
      return;
    }

    /* Typeahead. A second keystroke inside a second extends the search rather
       than restarting it, so "ma" finds "March" and not the first M again. */
    // The search box does this job properly when there is one, and would fight
    // it here: every character typed into it would also jump the cursor.
    if (!searchable && key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const now = event.timeStamp;
      typed.current.text = now - typed.current.at < 1_000 ? typed.current.text + key : key;
      typed.current.at = now;

      const prefix = typed.current.text.toLowerCase();
      const found = visible.findIndex(
        (item) => !item.disabled && textOf(item.label).toLowerCase().startsWith(prefix),
      );
      if (found !== -1) setActive(found);
    }
  };

  const selected = items.find((item) => item.value === value);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        role="combobox"
        aria-haspopup="listbox"
        {...aria}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-invalid={invalid ? 'true' : undefined}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={onKeyDown}
        className={cn(
          'w-full h-10 px-3 flex items-center gap-2 rounded-lg text-sm text-start',
          'bg-muted-foreground/10 outline-none transition-colors cursor-pointer',
          'hover:bg-muted-foreground/[0.16] focus-visible:ring-2 focus-visible:ring-primary/40',
          'aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-destructive/40',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          open && 'ring-2 ring-primary/40',
          className,
        )}
      >
        <span className={cn('grow truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.label : (placeholder ?? '')}
        </span>
        {/* Turns over when the list is open, so the control says which way it
            is about to move even before the list paints. */}
        <Icon
          name="chevronDown"
          size={16}
          className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={triggerRef} width={width}>
        {/*
          The box is a plain text field, not a second combobox: the trigger
          above already carries that role for the closed control, and two of
          them on one popup is a list a screen reader cannot name. It points at
          the active row itself, because while it is open it is the thing
          holding the focus.
        */}
        {searchable && (
          <div className="p-1 pb-0">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder ?? ''}
              aria-label={searchPlaceholder ?? aria['aria-label']}
              aria-controls={listId}
              aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
              autoComplete="off"
              className="w-full h-9 px-2.5 rounded-lg bg-muted-foreground/10 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            />
          </div>
        )}
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={aria['aria-label']}
          className="max-h-[min(20rem,60vh)] overflow-y-auto p-1"
        >
          {visible.map((item, index) => (
            <div
              key={item.value}
              id={`${listId}-${index}`}
              data-index={index}
              role="option"
              aria-selected={item.value === value}
              aria-disabled={item.disabled || undefined}
              /* Pointer, not focus: the trigger keeps the focus and points at
                 the active row through `aria-activedescendant`, so there is no
                 focus to hand back when the list closes. */
              onMouseEnter={() => !item.disabled && setActive(index)}
              onClick={() => pick(index)}
              className={cn(
                'flex items-center gap-2 px-2.5 min-h-11 sm:min-h-9 rounded-lg text-sm cursor-pointer',
                index === active && 'bg-muted',
                item.value === value && 'font-medium text-primary',
                item.disabled && 'opacity-50 pointer-events-none',
              )}
            >
              <span className="grow truncate">{item.label}</span>
              {item.value === value && <Icon name="check" size={14} className="shrink-0" />}
            </div>
          ))}
          {visible.length === 0 && (
            <div className="px-2.5 py-2 text-sm text-muted-foreground">
              {(needle && emptyText) || '—'}
            </div>
          )}
        </div>
      </AnchoredMenu>
    </>
  );
}
